import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { measurementsApi } from '../../api';
import { Flame, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { simulateHall } from '../../utils/hallModel';

const ACTIVITY_MULTIPLIERS = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  very_active: 1.9,
};

// Map server activity_level enum → calculator key
const ACTIVITY_MAP = {
  sedentary: 'sedentary',
  lightly_active: 'light',
  moderately_active: 'moderate',
  very_active: 'active',
  extremely_active: 'very_active',
};

function calcBMR({ sex, weight, height, age }) {
  if (!weight || !height || !age) return null;
  return sex === 'male'
    ? 10 * weight + 6.25 * height - 5 * age + 5
    : 10 * weight + 6.25 * height - 5 * age - 161;
}

export default function DeficitSurplusDialog({ open, onClose, client, targetKcal }) {
  const { t } = useTranslation();
  const [sex, setSex] = useState('male');
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [bfPct, setBfPct] = useState('');
  const [activity, setActivity] = useState('moderate');
  const [loading, setLoading] = useState(false);
  const [weeks, setWeeks] = useState(12);

  // Hydrate from client when dialog opens
  useEffect(() => {
    if (!open || !client) return;

    setSex(client.gender || 'male');
    setHeight(client.height_cm ? String(client.height_cm) : '');
    setActivity(ACTIVITY_MAP[client.activity_level] || 'moderate');

    if (client.birth_date) {
      const birthYear = new Date(client.birth_date).getFullYear();
      setAge(String(new Date().getFullYear() - birthYear));
    } else {
      setAge('');
    }

    setWeight('');
    setBfPct('');
    setLoading(true);
    measurementsApi.getAll({ user_id: client.id })
      .then(res => {
        const latest = (res.data || [])[0];
        if (latest?.weight_kg) setWeight(String(latest.weight_kg));
        if (latest?.body_fat_percent) setBfPct(String(latest.body_fat_percent));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, client]);

  const bmr = calcBMR({
    sex,
    weight: parseFloat(weight),
    height: parseFloat(height),
    age: parseFloat(age),
  });
  const tdee = bmr ? Math.round(bmr * (ACTIVITY_MULTIPLIERS[activity] || 1.55)) : null;
  const target = parseFloat(targetKcal) || 0;
  const delta = tdee != null && target > 0 ? target - tdee : null;
  const weeklyKg = delta != null ? +(delta * 7 / 7700).toFixed(2) : null;

  // Hall-model projection: forward-simulate the target intake for N weeks.
  const wNum = parseFloat(weight);
  const bfNum = parseFloat(bfPct);
  const canPredict = wNum > 0 && bfNum > 0 && bfNum < 70 && target > 0;
  const prediction = canPredict
    ? simulateHall({ weight: wNum, bfPct: bfNum, activity, intake: target, days: weeks * 7 })
    : null;
  const endWeight = prediction ? +prediction.endWeight.toFixed(1) : null;
  const endBf = prediction ? +prediction.endBfPct.toFixed(1) : null;
  const dWeight = prediction ? +(endWeight - wNum).toFixed(1) : null;
  const dBf = prediction ? +(endBf - bfNum).toFixed(1) : null;

  const isMaintain = delta != null && Math.abs(delta) <= 50;
  const isSurplus = delta != null && delta > 50;
  const tone = isMaintain ? 'maintain' : isSurplus ? 'surplus' : 'deficit';
  const toneColor = tone === 'surplus' ? '#FF5C00' : tone === 'deficit' ? '#22C55E' : '#94A3B8';
  const toneIcon = tone === 'surplus'
    ? <TrendingUp className="h-4 w-4" />
    : tone === 'deficit'
      ? <TrendingDown className="h-4 w-4" />
      : <Minus className="h-4 w-4" />;
  const toneLabel = tone === 'surplus'
    ? t('calculators.surplus')
    : tone === 'deficit'
      ? t('calculators.deficit')
      : t('calculators.maintenance');

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {t('calculators.deficitSurplusTitle')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Inputs */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">{t('calculators.sex')}</Label>
              <Select value={sex} onValueChange={setSex}>
                <SelectTrigger className="h-11 md:h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">{t('calculators.male')}</SelectItem>
                  <SelectItem value="female">{t('calculators.female')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-base md:text-xs">{t('calculators.age')}</Label>
              <Input className="h-9 text-base md:text-sm" type="number" value={age} onChange={e => setAge(e.target.value)} />
            </div>
            <div>
              <Label className="text-base md:text-xs">{t('calculators.weightKg')}</Label>
              <Input
                className="h-9 text-sm"
                type="number"
                value={weight}
                onChange={e => setWeight(e.target.value)}
                placeholder={loading ? '…' : ''}
              />
            </div>
            <div>
              <Label className="text-base md:text-xs">{t('calculators.heightCm')}</Label>
              <Input className="h-9 text-base md:text-sm" type="number" value={height} onChange={e => setHeight(e.target.value)} />
            </div>
            <div>
              <Label className="text-base md:text-xs">{t('calculators.bodyFatPct')}</Label>
              <Input
                className="h-9 text-sm"
                type="number"
                value={bfPct}
                onChange={e => setBfPct(e.target.value)}
                placeholder={loading ? '…' : ''}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">{t('calculators.activityLevel')}</Label>
            <Select value={activity} onValueChange={setActivity}>
              <SelectTrigger className="h-11 md:h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sedentary">{t('calculators.sedentary')}</SelectItem>
                <SelectItem value="light">{t('calculators.lightlyActive')}</SelectItem>
                <SelectItem value="moderate">{t('calculators.moderatelyActive')}</SelectItem>
                <SelectItem value="active">{t('calculators.veryActive')}</SelectItem>
                <SelectItem value="very_active">{t('calculators.extremelyActive')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Results */}
          <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><Flame className="h-3.5 w-3.5 text-primary" />{t('calculators.targetKcal')}</span>
              <span className="font-semibold text-foreground">{target > 0 ? `${Math.round(target)} kcal` : '—'}</span>
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>BMR</span>
              <span className="font-semibold text-foreground">{bmr ? `${Math.round(bmr)} kcal` : '—'}</span>
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>TDEE</span>
              <span className="font-semibold text-foreground">{tdee ? `${tdee} kcal` : '—'}</span>
            </div>

            {delta != null && (
              <div className="pt-2 mt-1 border-t border-border/60 flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide" style={{ color: toneColor }}>
                  {toneIcon}{toneLabel}
                </span>
                <div className="text-right">
                  <p className="text-base font-bold" style={{ color: toneColor }}>
                    {delta > 0 ? '+' : ''}{Math.round(delta)} kcal/day
                  </p>
                  {!isMaintain && (
                    <p className="text-xs text-muted-foreground">
                      {weeklyKg > 0 ? '+' : ''}{weeklyKg} kg/{t('calculators.week')}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Prediction block */}
            <div className="pt-2 mt-1 border-t border-border/60 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('calculators.predictionTitle')}
                </span>
                <div className="flex gap-1">
                  {[4, 8, 12, 16, 24].map(w => (
                    <button
                      key={w}
                      type="button"
                      onClick={() => setWeeks(w)}
                      className={`px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors ${weeks === w ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                    >
                      {w}w
                    </button>
                  ))}
                </div>
              </div>
              {prediction ? (
                <>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{t('calculators.predictedWeight')}</span>
                    <span className="text-foreground">
                      <span className="font-semibold">{endWeight} kg</span>
                      <span className={`ml-1.5 ${dWeight < 0 ? 'text-green-500' : dWeight > 0 ? 'text-primary' : 'text-muted-foreground'}`}>
                        ({dWeight > 0 ? '+' : ''}{dWeight} kg)
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{t('calculators.predictedBodyFat')}</span>
                    <span className="text-foreground">
                      <span className="font-semibold">{endBf}%</span>
                      <span className={`ml-1.5 ${dBf < 0 ? 'text-green-500' : dBf > 0 ? 'text-primary' : 'text-muted-foreground'}`}>
                        ({dBf > 0 ? '+' : ''}{dBf}%)
                      </span>
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-[11px] text-muted-foreground italic">
                  {t('calculators.enterBfToPredict')}
                </p>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
